import PropTypes from 'prop-types'
import { List, Map } from 'immutable'
import FormsyForm from 'formsy-react'
import { connect as connectRedux } from 'react-redux'
import ImmutablePropTypes from 'react-immutable-proptypes'
import React, {
  useCallback, useEffect, useState, useMemo,
} from 'react'
import { useMutation } from '@apollo/client'
import _parseInt from 'lodash/parseInt'
import { isPrimaryAccountProfile } from '../../services/user-account/index'
import { selectAccountOwnerProfile } from '../../services/user-profiles/selectors'
import { USER_INFO_UPDATE, USER_INFO_UPDATE_PROFILE } from '../../services/user-profiles/query'
import Support from '../Support/index'
import { TYPE_MULTIPLE_PROFILES_RM_USER } from '../../services/dialog/index'
import { TextInput, EmailInput } from '../FormInput.v2/index'
import Sherpa, { TYPE_SMALL_BLUE } from '../Sherpa/index'
import { URL_ACCOUNT_PROFILE } from '../../services/url/constants'
import { MAX_PROFILES } from '../../services/user-profiles/index'
import { getUserAvatarName } from '../../services/user/index'
import UserAvatar from '../UserAvatar/index'
import { H2, H3 } from '../Heading/index'
import { getBoundActions } from '../../actions/index'
import { Card } from '../Card/index'
import { selectProfilePicturesMap } from '../../services/community/identity/profile/redux/selectors'
import useCommunityEnabled from '../../services/community/shared/useCommunityEnabled'
import Link from '../Link/index'
import Button, { TYPES } from '../Button/index'

function getProfileName (profile) {
  const firstName = profile.get('first_name')
  const lastName = profile.get('last_name') ? profile.get('last_name') : ''
  return !firstName ? '' : `${firstName} ${lastName}`
}

// True when this profile row is the account owner (main account).
function profileIsAccountOwner (profile) {
  if (!Map.isMap(profile)) return false
  const uid = profile.get('uid')
  const ownerUid = profile.get('account_owner_uid')
  if (uid == null || ownerUid == null) return false
  return String(uid) === String(ownerUid)
}

// Update the profile name using the appropriate GraphQL mutation.
async function runProfileNameUpdate ({
  profile,
  firstName,
  lastName,
  updateOwnUserInfo,
  updateChildProfileName,
}) {
  const input = {
    firstName: firstName || undefined,
    lastName: lastName || undefined,
  }

  if (profileIsAccountOwner(profile)) {
    const result = await updateOwnUserInfo({ variables: { input } })
    const userInfoUpdate = result && result.data && result.data.userInfoUpdate
    return {
      ok: !!(userInfoUpdate && userInfoUpdate.success),
      profileUuid: profile.get('uuid'),
    }
  }

  const profileUuid = profile.get('uuid')
  if (!profileUuid) {
    return { ok: false, profileUuid: undefined }
  }

  const result = await updateChildProfileName({
    variables: {
      input: {
        profileUuid,
        ...input,
      },
    },
  })

  const userInfoUpdateProfile = result && result.data && result.data.userInfoUpdateProfile
  return {
    ok: !!(userInfoUpdateProfile && userInfoUpdateProfile.success),
    profileUuid,
  }
}

function ManageProfilesEditSection ({
  profile,
  staticText,
  editFirstName,
  editLastName,
  onEditFirstNameChange,
  onEditLastNameChange,
  onSaveEdit,
  onCancelEdit,
  saveLoading,
}) {
  const handleFormChange = useCallback((model) => {
    if (model.editFirstName !== undefined) {
      onEditFirstNameChange(model.editFirstName)
    }
    if (model.editLastName !== undefined) {
      onEditLastNameChange(model.editLastName)
    }
  }, [onEditFirstNameChange, onEditLastNameChange])

  const editTitle = staticText
    .getIn(['data', 'editProfileFor'])
    .replace('{email}', profile.get('email') || '')

  return (
    <div className="user-profile__edit-section">
      <H3 className="user-profile__edit-heading">
        {editTitle}
      </H3>
      <FormsyForm
        className="user-profile__edit-form"
        onChange={handleFormChange}
        onValidSubmit={(model) => {
          onSaveEdit(profile, model.editFirstName, model.editLastName)
        }}
      >
        <TextInput
          label={staticText.getIn(['data', 'firstName'])}
          value={editFirstName}
          name="editFirstName"
          required
        />
        <TextInput
          label={staticText.getIn(['data', 'lastName'])}
          value={editLastName}
          name="editLastName"
        />
        <div className="user-profile__edit-actions">
          <Button
            type={TYPES.PRIMARY}
            submit
            disabled={saveLoading}
            className="user-profile__edit-button user-profile__edit-save"
          >
            {staticText.getIn(['data', 'save'])}
          </Button>
          <Button
            type={TYPES.SECONDARY}
            onClick={onCancelEdit}
            disabled={saveLoading}
            className="user-profile__edit-button user-profile__edit-cancel"
          >
            {staticText.getIn(['data', 'cancel'])}
          </Button>
        </div>
      </FormsyForm>
    </div>
  )
}

function renderProfiles ({
  auth,
  user,
  profiles,
  userProfiles,
  staticText,
  bootstrapComplete,
  showModal,
  communityProfilePictures,
  communityEnabled,
  isAccountOwner,
  editingProfileUid,
  editFirstName,
  editLastName,
  onEditFirstNameChange,
  onEditLastNameChange,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  saveLoading,
}) {
  const accountOwner = selectAccountOwnerProfile({ userProfiles })
  const isOwner = isPrimaryAccountProfile({ user }) ? _parseInt(accountOwner.get('uid')) === auth.get('uid') : false
  const remaining = MAX_PROFILES - profiles.size
  const client = process.env.BROWSER && bootstrapComplete
  return (
    <div className="profile-section">
      <p className="profile-section__note">
        {client ? `${staticText.getIn(['data', 'youAreAble']).replace('{count}', remaining)}` : null}
      </p>
      {
        profiles.map((profile) => {
          const name = getProfileName(profile)
          const userAvatarName = getUserAvatarName(profile)
          const owner = profileIsAccountOwner(profile)
          const profileUid = String(profile.get('uid'))
          const isCurrentProfile = communityEnabled
            && String(profile.get('uid')) === String(auth.get('uid'))
          const avatarPath = communityEnabled
            ? communityProfilePictures.getIn([profileUid, 'card'], '')
            : profile.getIn(['profilePicture', 'hdtv_190x266'], '')
          const isEditing = editingProfileUid !== null && String(editingProfileUid) === profileUid

          return (
            <Card key={profile.get('email')}>
              <div className="user-profile">
                {isCurrentProfile ? (
                  <Link
                    className="user-profile__username user-profile__username--link"
                    to={`/profile/${profile.get('uuid')}`}
                  >
                    <UserAvatar
                      path={avatarPath}
                      className={['user-profile__avatar']}
                      name={userAvatarName}/>
                    {profile.get('username')}
                  </Link>
                ) : (
                  <div className="user-profile__username">
                    <UserAvatar
                      path={avatarPath}
                      className={['user-profile__avatar']}
                      name={userAvatarName}/>
                    {profile.get('username')}
                  </div>
                )}
                { (!name)
                  ? <div className="user-profile__realname missing">
                    { isAccountOwner && <Link to={communityEnabled
                      ? `/profile/${auth.get('uuid')}`
                      : URL_ACCOUNT_PROFILE}
                        >
                          {staticText.getIn(['data', 'updateName'])}
                        </Link>
                      }
                  </div>
                  : <div className="user-profile__cell">
                    <div className="user-profile__name-label">
                      {staticText.getIn(['data', 'name'])}
                    </div>
                    <div className="user-profile__realname">
                      { name }
                    </div>
                  </div>
                }
                <div className="user-profile__cell">
                  <div className="user-profile__email-label">
                    {staticText.getIn(['data', 'email'])}
                  </div>
                  <div className="user-profile__email">
                    {profile.get('email')}
                  </div>
                </div>
                { isOwner
                  ? ownerAccess({
                    profile, staticText, showModal, owner, onStartEdit,
                  })
                  : userAccess(staticText, owner)}
              </div>
              { isEditing && (
                <ManageProfilesEditSection
                  profile={profile}
                  staticText={staticText}
                  editFirstName={editFirstName}
                  editLastName={editLastName}
                  onEditFirstNameChange={onEditFirstNameChange}
                  onEditLastNameChange={onEditLastNameChange}
                  onSaveEdit={onSaveEdit}
                  onCancelEdit={onCancelEdit}
                  saveLoading={saveLoading}
                />
              )}
            </Card>
          )
        })
      }
    </div>
  )
}

function ownerAccess ({
  profile,
  staticText,
  showModal,
  owner,
  onStartEdit,
}) {
  return (
    owner
      ? (
        <div className="user-profile__cell user-profile__cell-actions">
          <div className="user-profile__main-account">
            {staticText.getIn(['data', 'mainAccount'])}
          </div>
          <span className="user-profile__actions-sep">|</span>
          <Button
            type={TYPES.LINK}
            className="user-profile__edit-link"
            onClick={() => onStartEdit(profile)}
          >
            {staticText.getIn(['data', 'edit'])}
          </Button>
        </div>
      )
      : (
        <div className="user-profile__cell user-profile__cell-actions">
          <Button
            type={TYPES.LINK}
            className="user-profile__actions"
            onClick={() => showModal(profile)}
          >
            {staticText.getIn(['data', 'remove'])}
          </Button>
          <span className="user-profile__actions-sep">|</span>
          <Button
            type={TYPES.LINK}
            className="user-profile__edit-link"
            onClick={() => onStartEdit(profile)}
          >
            {staticText.getIn(['data', 'edit'])}
          </Button>
        </div>
      )
  )
}

function userAccess (staticText, owner) {
  return (
    owner
      ? <div className="user-profile__cell">
        <div className="main-account">
          {staticText.getIn(['data', 'mainAccount'])}
        </div>
      </div>
      : <div className="user-profile__cell user-profile__cell-empty" />
  )
}

function ManageProfilesPage ({
  communityProfilePictures,
  bootstrapComplete,
  createProfile,
  removeProfile,
  fetchProfilePictures,
  userProfiles,
  staticText,
  auth,
  user,
  renderModal,
  removeModal,
  getProfiles,
  trackProfileNameUpdatedEmarsys,
}) {
  const { enabled: communityEnabled } = useCommunityEnabled()
  const profiles = userProfiles.get('data', List())
  const accountOwner = selectAccountOwnerProfile({ userProfiles })
  const isAccountOwner = isPrimaryAccountProfile({ user })
    ? _parseInt(accountOwner.get('uid')) === auth.get('uid')
    : false

  const mutationContext = useMemo(() => ({ jwt: auth.get('jwt') }), [auth])

  const [updateOwnUserInfo, { loading: saveOwnerLoading }] = useMutation(
    USER_INFO_UPDATE,
    { context: mutationContext },
  )

  const [updateChildProfileName, { loading: saveChildLoading }] = useMutation(
    USER_INFO_UPDATE_PROFILE,
    { context: mutationContext },
  )

  const saveLoading = saveOwnerLoading || saveChildLoading

  const [editingProfileUid, setEditingProfileUid] = useState(null)
  const [editFirstName, setEditFirstName] = useState('')
  const [editLastName, setEditLastName] = useState('')

  const onStartEdit = useCallback((profile) => {
    setEditingProfileUid(profile.get('uid'))
    setEditFirstName(profile.get('first_name') || '')
    setEditLastName(profile.get('last_name') || '')
  }, [])

  const onCancelEdit = useCallback(() => {
    setEditingProfileUid(null)
    setEditFirstName('')
    setEditLastName('')
  }, [])

  const onSaveEdit = useCallback(async (profile, firstName, lastName) => {
    try {
      const { ok, profileUuid } = await runProfileNameUpdate({
        profile,
        firstName,
        lastName,
        updateOwnUserInfo,
        updateChildProfileName,
      })

      if (!ok) return

      getProfiles(auth)
      trackProfileNameUpdatedEmarsys({
        profileUid: profile.get('uid'),
        profileUuid,
      })
      setEditingProfileUid(null)
      setEditFirstName('')
      setEditLastName('')
    } catch {
      // mutation failed; no inline error for name update
    }
  }, [auth])

  // 1.1: Fetch Brooklyn avatars for all profiles when flag is ON.
  // Profiles are server-side hydrated so USER_PROFILES_SET never dispatches;
  // the auto-fetch watcher never fires. Trigger it explicitly here.
  useEffect(() => {
    if (!communityEnabled) return
    const uids = profiles.map((p) => String(p.get('uid'))).filter(Boolean).toJS()
    if (uids.length > 0) {
      fetchProfilePictures(uids)
    }
  }, [profiles])
  const profileError = userProfiles.get('error')
  const created = userProfiles.get('created')
  const creating = userProfiles.get('creating', false)
  const remaining = MAX_PROFILES - profiles.size

  const [formIsValid, setFormIsValid] = useState(false)
  const [formFields, setFormFields] = useState({
    firstName: '',
    lastName: '',
    email: '',
  })

  const onChange = useCallback((model) => {
    setFormFields(model)
  }, [])

  const onSubmit = useCallback(() => {
    createProfile({
      user_account_id: auth.get('userAccountId'),
      first_name: formFields.firstName,
      last_name: formFields.lastName,
      email: formFields.email,
      language: user.getIn(['data', 'language']),
      auth,
    })
  }, [formFields])

  const resetForm = useCallback(() => {
    setFormFields({ firstName: '', lastName: '', email: '' })
  }, [])

  const setValid = useCallback(() => {
    setFormIsValid(true)
  }, [])

  const setInvalid = useCallback(() => {
    setFormIsValid(false)
  }, [])

  const validations = useMemo(() => ({
    isEmail: staticText.getIn(['data', 'emailValidError']),
  }), [staticText])

  const showModal = useCallback((profile) => {
    renderModal(TYPE_MULTIPLE_PROFILES_RM_USER, {
      profileToRemove: profile,
      titleText: staticText.getIn(['data', 'areYouSure']),
      bodyText: staticText.getIn(['data', 'removeProfile']),
      buttonText: staticText.getIn(['data', 'deleteProfile']),
      auth,
      removeProfile,
      removeModal,
    })
  })

  useEffect(() => {
    if (creating === false && created !== false) resetForm()
    if (created === false) setInvalid()
  }, [creating])

  return (
    <div className="manage-profile-page">
      <div className="manage-profile">
        <H2>{staticText.getIn(['data', 'title'])}</H2>
        {renderProfiles({
          auth,
          user,
          userProfiles,
          profiles,
          staticText,
          bootstrapComplete,
          showModal,
          communityProfilePictures,
          communityEnabled,
          isAccountOwner,
          editingProfileUid,
          editFirstName,
          editLastName,
          onEditFirstNameChange: setEditFirstName,
          onEditLastNameChange: setEditLastName,
          onStartEdit,
          onCancelEdit,
          onSaveEdit,
          saveLoading,
        }) }
        <div className="manage-profile__add-form-container">
          {
            creating
              ? <div className="manage-profile__processing">
                <Sherpa className={['manage-profile__sherpa']} type={TYPE_SMALL_BLUE} />
                <div className="manage-profile__processing-text">
                  {`${staticText.getIn(['data', 'adding'])}...`}
                </div>
              </div>
              : null
          }
          {
            profileError
              ? <div className="manage-profile__error">
                { staticText.getIn(['data', 'genericError']) }
              </div> : null
          }
          {
            remaining > 0
              ? <FormsyForm
                className="manage-profile__form form--inline"
                onValidSubmit={onSubmit}
                onValid={setValid}
                onInvalid={setInvalid}
                onChange={onChange}
              >
                <div className="manage-profile__add-form">
                  <div>
                    <div className="manage-profile__add-profile">
                      <UserAvatar
                        className={['user-profile__avatar']}
                        name=" "
                      />
                      <div className="manage-profile__add-profile-title">
                        <H3 className="manage-profile__add-title">{staticText.getIn(['data', 'addProfile'])}</H3>
                      </div>
                    </div>
                    <TextInput
                      label={staticText.getIn(['data', 'firstName'])}
                      value={formFields.firstName}
                      autocomplete="off"
                      name="firstName"
                      required
                    />
                  </div>
                  <div>
                    <TextInput
                      label={staticText.getIn(['data', 'lastName'])}
                      value={formFields.lastName}
                      autocomplete="off"
                      name="lastName"
                    />
                  </div>
                  <div>
                    <EmailInput
                      note={staticText.getIn(['data', 'forPersonalized'])}
                      label={staticText.getIn(['data', 'emailAddress'])}
                      validationErrors={validations}
                      value={formFields.email}
                      validations="isEmail"
                      name="email"
                      required
                    />
                  </div>
                  <div className="manage-profile__add-profile-btn">
                    <Button
                      type={TYPES.PRIMARY}
                      submit
                      className="manage-profile__form-button"
                      disabled={!formIsValid}
                      stacked
                    >
                      {staticText.getIn(['data', 'addProfile'])}
                    </Button>
                  </div>
                  <div className="manage-profile__add-btn">
                    <Button
                      type={TYPES.PRIMARY}
                      submit
                      className="manage-profile__form-button"
                      disabled={!formIsValid}
                      stacked
                    >
                      {staticText.getIn(['data', 'add'])}
                    </Button>
                  </div>
                </div>
              </FormsyForm> : null
          }
        </div>
      </div>
      <Support />
    </div>
  )
}

ManageProfilesPage.propTypes = {
  userProfiles: ImmutablePropTypes.map.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  createProfile: PropTypes.func.isRequired,
  removeProfile: PropTypes.func.isRequired,
  fetchProfilePictures: PropTypes.func.isRequired,
  renderModal: PropTypes.func.isRequired,
  removeModal: PropTypes.func.isRequired,
  getProfiles: PropTypes.func.isRequired,
  trackProfileNameUpdatedEmarsys: PropTypes.func.isRequired,
  bootstrapComplete: PropTypes.bool,
}

export default connectRedux(
  (state) => ({
    communityProfilePictures: selectProfilePicturesMap(state),
    staticText: state.staticText.getIn(['data', 'manageProfilesPage'], Map()),
    bootstrapComplete: state.app.get('bootstrapComplete'),
    userProfiles: state.userProfiles,
    auth: state.auth,
    user: state.user,
  }),
  (dispatch) => {
    const actions = getBoundActions(dispatch)
    return {
      renderModal: actions.dialog.renderModal,
      removeModal: actions.dialog.removeModal,
      createProfile: actions.userProfiles.createProfile,
      removeProfile: actions.userProfiles.removeProfile,
      fetchProfilePictures: actions.communityProfile.fetchProfilePictures,
      getProfiles: actions.userProfiles.getProfiles,
      trackProfileNameUpdatedEmarsys: actions.userProfiles.trackProfileNameUpdatedEmarsys,
    }
  },
)(ManageProfilesPage)
